import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Newspaper, Inbox, Settings as SettingsIcon, LogOut, Image as ImageIcon, ExternalLink, Menu } from 'lucide-react';
import { api } from '../lib/api';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import AdminLogin from './AdminLogin';
import Dashboard from './Dashboard';
import ContentEditor from './ContentEditor';
import BlogManager from './BlogManager';
import Submissions from './Submissions';
import AdminSettings from './AdminSettings';
import MediaLibrary from './MediaLibrary';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/content', label: 'Site Content', icon: FileText },
  { to: '/admin/blog', label: 'News & Blog', icon: Newspaper },
  { to: '/admin/submissions', label: 'Form Submissions', icon: Inbox },
  { to: '/admin/media', label: 'Media Library', icon: ImageIcon },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon },
];

function SidebarNav({ onNavigate, onLogout }) {
  return (
    <>
      <div className="px-6 py-6 border-b border-white/15">
        <h1 className="font-extrabold text-lg">Intexso CMS</h1>
        <p className="text-white/70 text-xs mt-1">Content Management</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                isActive ? 'bg-white text-[#26247B]' : 'text-white/85 hover:bg-white/10'
              }`
            }
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/15 space-y-1">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-white/85 hover:bg-white/10 transition-colors duration-200">
          <ExternalLink className="h-4 w-4" /> View Site
        </a>
        <button
          onClick={onLogout}
          data-testid="admin-logout-button"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-white/85 hover:bg-white/10 transition-colors duration-200"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </>
  );
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('intexso_admin_token'));
  const [checking, setChecking] = useState(authed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authed) return;
    api
      .get('/admin/me')
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem('intexso_admin_token');
        setAuthed(false);
        setChecking(false);
      });
  }, [authed]);

  const logout = () => {
    localStorage.removeItem('intexso_admin_token');
    setAuthed(false);
    navigate('/admin');
  };

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F6F7F9] text-[#26247B] font-semibold">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F6F7F9] flex">
      {/* Desktop sidebar */}
      <aside data-testid="admin-sidebar" className="hidden md:flex w-64 bg-[#26247B] text-white flex-col shrink-0 min-h-screen sticky top-0 max-h-screen">
        <SidebarNav onLogout={logout} />
      </aside>

      {/* Mobile slide-in sidebar */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          data-testid="admin-mobile-sidebar"
          className="w-72 max-w-[85vw] bg-[#26247B] text-white flex flex-col p-0 border-none gap-0"
        >
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <SidebarNav onNavigate={() => setMobileNavOpen(false)} onLogout={logout} />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 bg-[#26247B] text-white px-4 py-3.5 shadow-sm">
          <button
            onClick={() => setMobileNavOpen(true)}
            data-testid="admin-mobile-menu-button"
            aria-label="Open menu"
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors duration-200"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-extrabold text-base">Intexso CMS</h1>
        </div>

        <main className="flex-1 min-w-0 p-4 sm:p-6 md:p-8 overflow-x-hidden">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/content" element={<ContentEditor />} />
            <Route path="/content/:sectionKey" element={<ContentEditor />} />
            <Route path="/blog" element={<BlogManager />} />
            <Route path="/submissions" element={<Submissions />} />
            <Route path="/media" element={<MediaLibrary />} />
            <Route path="/settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
